import {BaseButton, BaseCard, BaseInput, BaseModal, BaseBadge, BasePagination, BaseTable} from "@/components";
import type {Column} from "@/components/BaseTable/BaseTable";
import {
  useCreateExchangeRate,
  useDeleteExchangeRate,
  useExchangeRates,
  useUpdateExchangeRate,
} from "@/hooks/useExchangeRates";
import type {ExchangeRate} from "@/services/exchange-rates.service";
import {Pause, Pencil, Play, Trash2} from "lucide-react";
import {useState} from "react";

const formatDate = (iso: string): string => {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("es-CL", {dateStyle: "short", timeStyle: "short"}).format(new Date(iso));
};

export function ExchangeRates() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<ExchangeRate | null>(null);

  const offset = (page - 1) * limit;
  const {data, isLoading} = useExchangeRates({limit, offset});
  const deleteRate = useDeleteExchangeRate();
  const updateRate = useUpdateExchangeRate();

  const items = data?.items ?? [];
  const pagination = data?.pagination ?? {total: 0, page: 1, limit: 20, totalPages: 1};

  const columns: Column<ExchangeRate>[] = [
    {
      key: "pair",
      header: "Par",
      render: row => (
        <span className="text-sm font-semibold text-(--color-text-primary)">
          {row.from_currency} → {row.to_currency}
        </span>
      ),
    },
    {
      key: "buy_rate",
      header: "Tasa Compra",
      render: row => <span className="text-sm font-mono tabular-nums text-(--color-text-primary)">{row.buy_rate}</span>,
    },
    {
      key: "sell_rate",
      header: "Tasa Venta",
      render: row => (
        <span className="text-sm font-mono tabular-nums text-(--color-text-primary)">{row.sell_rate}</span>
      ),
    },
    {
      key: "spread_pct",
      header: "Spread",
      render: row => <span className="text-sm text-(--color-text-secondary)">{row.spread_pct}%</span>,
    },
    {
      key: "is_active",
      header: "Estado",
      render: row => (
        <BaseBadge
          variant={row.is_active ? "success" : "neutral"}
          label={row.is_active ? "Activo" : "Inactivo"}
          dot
        />
      ),
    },
    {
      key: "updated_at",
      header: "Actualizado",
      render: row => <span className="text-xs text-(--color-text-tertiary)">{formatDate(row.updated_at)}</span>,
    },
    {
      key: "actions",
      header: "Acciones",
      align: "right",
      render: row => (
        <div className="flex items-center gap-1 justify-end">
          <BaseButton
            variant="ghost"
            size="sm"
            onClick={() => setEditing(row)}
            title="Editar"
          >
            <Pencil size={14} />
          </BaseButton>
          <BaseButton
            variant="ghost"
            size="sm"
            onClick={() => updateRate.mutate({id: row.id, body: {is_active: !row.is_active}})}
            title={row.is_active ? "Desactivar" : "Activar"}
          >
            {row.is_active ? <Pause size={14} /> : <Play size={14} />}
          </BaseButton>
          <BaseButton
            variant="ghost"
            size="sm"
            onClick={() => deleteRate.mutate(row.id)}
            title="Eliminar"
          >
            <Trash2 size={14} />
          </BaseButton>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-(--color-text-primary)">Tasas de Cambio</h1>
          <p className="text-sm text-(--color-text-secondary) mt-1">{pagination.total} tasas configuradas</p>
        </div>
        <BaseButton
          variant="primary"
          size="md"
          onClick={() => setShowCreate(true)}
        >
          + Nueva tasa
        </BaseButton>
      </div>

      <BaseCard padding="none">
        <BaseTable<ExchangeRate>
          data={items}
          columns={columns}
          loading={isLoading}
          emptyMessage="No hay tasas configuradas."
        />
        {pagination.totalPages > 1 && (
          <BasePagination
            page={page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            limit={limit}
            onPageChange={setPage}
          />
        )}
      </BaseCard>

      {showCreate && <CreateExchangeRateModal onClose={() => setShowCreate(false)} />}
      {editing && (
        <EditExchangeRateModal
          rate={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function CreateExchangeRateModal({onClose}: {onClose: () => void}) {
  const createRate = useCreateExchangeRate();
  const [form, setForm] = useState({from_currency: "", to_currency: "", buy_rate: "", sell_rate: ""});
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError("");
    if (!form.from_currency || !form.to_currency || !form.buy_rate || !form.sell_rate) {
      setError("Todos los campos son requeridos");
      return;
    }
    try {
      await createRate.mutateAsync({
        from_currency: form.from_currency.toUpperCase(),
        to_currency: form.to_currency.toUpperCase(),
        buy_rate: form.buy_rate,
        sell_rate: form.sell_rate,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al crear la tasa");
    }
  };

  const set = (key: string, value: string) => setForm(prev => ({...prev, [key]: value}));

  return (
    <BaseModal
      open
      onClose={onClose}
      title="Nueva Tasa de Cambio"
      size="md"
      footer={
        <>
          <BaseButton
            variant="ghost"
            size="md"
            onClick={onClose}
          >
            Cancelar
          </BaseButton>
          <BaseButton
            variant="primary"
            size="md"
            loading={createRate.isPending}
            onClick={handleSubmit}
          >
            Crear
          </BaseButton>
        </>
      }
    >
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4"
      >
        <div className="grid grid-cols-2 gap-3">
          <BaseInput
            label="Moneda Origen"
            placeholder="USD"
            value={form.from_currency}
            onChange={e => set("from_currency", e.target.value.toUpperCase())}
          />
          <BaseInput
            label="Moneda Destino"
            placeholder="EUR"
            value={form.to_currency}
            onChange={e => set("to_currency", e.target.value.toUpperCase())}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <BaseInput
            label="Tasa Compra (buy_rate)"
            placeholder="0.910000"
            value={form.buy_rate}
            onChange={e => set("buy_rate", e.target.value)}
          />
          <BaseInput
            label="Tasa Venta (sell_rate)"
            placeholder="0.920000"
            value={form.sell_rate}
            onChange={e => set("sell_rate", e.target.value)}
          />
        </div>
        {error && <p className="text-sm text-(--color-danger)">{error}</p>}
      </form>
    </BaseModal>
  );
}

function EditExchangeRateModal({rate, onClose}: {rate: ExchangeRate; onClose: () => void}) {
  const updateRate = useUpdateExchangeRate();
  const [form, setForm] = useState({buy_rate: rate.buy_rate, sell_rate: rate.sell_rate});
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError("");
    try {
      await updateRate.mutateAsync({id: rate.id, body: {buy_rate: form.buy_rate, sell_rate: form.sell_rate}});
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al actualizar");
    }
  };

  const set = (key: string, value: string) => setForm(prev => ({...prev, [key]: value}));

  return (
    <BaseModal
      open
      onClose={onClose}
      title={`Editar — ${rate.from_currency}/${rate.to_currency}`}
      size="md"
      footer={
        <>
          <BaseButton
            variant="ghost"
            size="md"
            onClick={onClose}
          >
            Cancelar
          </BaseButton>
          <BaseButton
            variant="primary"
            size="md"
            loading={updateRate.isPending}
            onClick={handleSubmit}
          >
            Guardar
          </BaseButton>
        </>
      }
    >
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4"
      >
        <div className="grid grid-cols-2 gap-3 p-3 rounded-md bg-(--color-surface-2) text-xs text-(--color-text-secondary)">
          <div>
            <span className="font-medium">Origen:</span> {rate.from_currency}
          </div>
          <div>
            <span className="font-medium">Destino:</span> {rate.to_currency}
          </div>
          <div>
            <span className="font-medium">Spread:</span> {rate.spread_pct}%
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <BaseInput
            label="Tasa Compra"
            value={form.buy_rate}
            onChange={e => set("buy_rate", e.target.value)}
          />
          <BaseInput
            label="Tasa Venta"
            value={form.sell_rate}
            onChange={e => set("sell_rate", e.target.value)}
          />
        </div>
        {error && <p className="text-sm text-(--color-danger)">{error}</p>}
      </form>
    </BaseModal>
  );
}
